'use client';

import { Badge, BodyDefault, MetaDefault } from '@terra/ui-design-system';
import { useDateFormatter } from '@terra/ui-formatters';
import { Activity01Icon, CrownIcon, Key01Icon } from '@terra/ui-icons';
import { Grid, HStack, VStack, token } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import {
  useInternalNetworkDiscoveryRuns,
  useInternalNetworkOwnedPrincipals,
} from '../../../hooks/api/useInternalNetworkOverview';
import { useInternalNetworkCrownJewels } from '../../../hooks/api/useInternalNetworkScenarios';
import { DashboardCard } from '../../dashboard/DashboardCard';

const LIST_MAX_HEIGHT = 240;
const EMPTY_HEIGHT = 120;
const RECENT_DISCOVERY_RUNS = 5;
const MUTED_ICON_COLOR = token('colors.fg.neutral.muted');

type ReferenceRow = {
  id: string;
  leading: ReactNode;
  trailing: ReactNode;
};

const entityRow = ({
  id,
  label,
  kind,
}: {
  id: string;
  label: string;
  kind: string;
}): ReferenceRow => ({
  id,
  leading: (
    <BodyDefault color="fg.neutral.default" truncate>
      {label}
    </BodyDefault>
  ),
  trailing: (
    <Badge size="sm" variant="outline">
      {kind}
    </Badge>
  ),
});

export function InternalNetworkReferenceLists({
  networkId,
}: {
  networkId: string;
}) {
  const t = useTranslations('home.networkReference');
  const dateFormatter = useDateFormatter();
  const { crownJewels, isLoading: isCrownJewelsLoading } =
    useInternalNetworkCrownJewels({ networkId });
  const { ownedPrincipals, isLoading: isOwnedPrincipalsLoading } =
    useInternalNetworkOwnedPrincipals({ networkId });
  const { discoveryRuns, isLoading: isDiscoveryRunsLoading } =
    useInternalNetworkDiscoveryRuns({ networkId });

  const crownJewelRows = crownJewels.map((jewel) =>
    entityRow({
      id: jewel.id,
      label: jewel.label ?? jewel.entityId,
      kind: jewel.entityKind,
    }),
  );
  const ownedPrincipalRows = ownedPrincipals.map((principal) =>
    entityRow({
      id: principal.id,
      label: principal.label ?? principal.entityId,
      kind: principal.entityKind,
    }),
  );
  const discoveryRunRows: ReferenceRow[] = discoveryRuns
    .slice(0, RECENT_DISCOVERY_RUNS)
    .map((run) => ({
      id: run.id,
      leading: (
        <Badge size="sm" variant="outline">
          {t(`discoveryStatus.${run.status}`)}
        </Badge>
      ),
      trailing: (
        <MetaDefault color="fg.neutral.muted">
          {dateFormatter.format(new Date(run.createdAt))}
        </MetaDefault>
      ),
    }));

  return (
    <VStack gap={4} w="full" alignItems="stretch">
      <Grid w="full" columns={{ base: 1, lg: 2 }} gap={4} alignItems="stretch">
        <ReferenceListCard
          title={t('crownJewels')}
          emptyLabel={t('noCrownJewels')}
          icon={<CrownIcon size={16} color={MUTED_ICON_COLOR} />}
          rows={crownJewelRows}
          isLoading={isCrownJewelsLoading}
        />
        <ReferenceListCard
          title={t('ownedPrincipals')}
          emptyLabel={t('noOwnedPrincipals')}
          icon={<Key01Icon size={16} color={MUTED_ICON_COLOR} />}
          rows={ownedPrincipalRows}
          isLoading={isOwnedPrincipalsLoading}
        />
      </Grid>
      <ReferenceListCard
        title={t('discoveryHistory')}
        emptyLabel={t('noDiscoveryRuns')}
        icon={<Activity01Icon size={16} color={MUTED_ICON_COLOR} />}
        rows={discoveryRunRows}
        isLoading={isDiscoveryRunsLoading}
      />
    </VStack>
  );
}

function ReferenceListCard({
  title,
  emptyLabel,
  icon,
  rows,
  isLoading,
}: {
  title: string;
  emptyLabel: string;
  icon: ReactNode;
  rows: ReferenceRow[];
  isLoading: boolean;
}) {
  return (
    <DashboardCard
      title={title}
      icon={icon}
      h="full"
      isLoading={isLoading}
      isEmpty={!isLoading && rows.length === 0}
      emptyLabel={emptyLabel}
      contentHeight={EMPTY_HEIGHT}
    >
      <VStack
        gap={0}
        alignItems="stretch"
        w="full"
        maxH={LIST_MAX_HEIGHT}
        overflowY="auto"
      >
        {rows.map((row, index) => (
          <HStack
            key={row.id}
            justify="space-between"
            gap={3}
            py={2}
            w="full"
            minW={0}
            borderTopWidth={index > 0 ? '1px' : 0}
            borderTopStyle="solid"
            borderTopColor="border.neutral.subtle"
          >
            {row.leading}
            {row.trailing}
          </HStack>
        ))}
      </VStack>
    </DashboardCard>
  );
}
